import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	Alert,
	Button,
	Col,
	Modal,
	Progress,
	Row,
	Space,
	Spin,
	Typography,
	Upload,
} from 'antd';
import {
	FileSyncOutlined,
	InboxOutlined,
} from '@ant-design/icons';
import { getSessionUser } from "../../../utils/auth/sessionUser";
import {
	fetchXmlImportStatus,
	submitXmlImport,
	XmlImportJobStatus,
} from '../../../api/healthcare/PatientApi';
import { useNotifications } from '../../../context/NotificationContext';
import ImportCarousel from './ImportCarousel';
import ImportSuccessModal from './ImportSuccessModal';

const { Dragger } = Upload;
const { Text, Title } = Typography;

interface Props {
	open: boolean;
	onClose: () => void;
	/** 'new_patient' → creates a new account. 'existing_patient' → maps into an existing patient. */
	mode: 'new_patient' | 'existing_patient';
	/** Required when mode = 'existing_patient'. */
	existingPatientUuid?: string;
	existingPatientName?: string;
	/** Called after a successful (completed/partial) import. */
	onImportComplete?: (jobStatus: XmlImportJobStatus) => void;
}

type Stage = 'select' | 'uploading' | 'polling' | 'done';

const POLL_INTERVAL_MS = 3000;

// Mirrors the server's `max:51200` (KB) on the upload route — a guard only, the server decides.
const MAX_FILE_BYTES = 50 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['.pdf', '.xml', '.txt'];

const extensionOf = (filename: string): string => {
	const dot = filename.lastIndexOf('.');
	return dot === -1 ? '' : filename.slice(dot).toLowerCase();
};

const detectFormat = (filename: string): 'md_xml' | 'pdf' =>
	extensionOf(filename) === '.pdf' ? 'pdf' : 'md_xml';

const FORMAT_LABELS: Record<'md_xml' | 'pdf', string> = {
	md_xml: 'Medical Director XML export',
	pdf: 'PDF patient record',
};

const MedicalDirectorImportModal: React.FC<Props> = ({
	open,
	onClose,
	mode,
	existingPatientUuid,
	existingPatientName,
	onImportComplete,
}) => {
	const [stage, setStage] = useState<Stage>('select');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [uploading, setUploading] = useState(false);
	const [jobStatus, setJobStatus] = useState<XmlImportJobStatus | null>(null);
	const [pollError, setPollError] = useState<string | null>(null);
	const [completedJobStatus, setCompletedJobStatus] = useState<XmlImportJobStatus | null>(null);
	const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { showError, showSuccess } = useNotifications();

	const company = getSessionUser().company as string;

	// Reset when modal opens/closes
	useEffect(() => {
		if (open) {
			setStage('select');
			setSelectedFile(null);
			setUploading(false);
			setJobStatus(null);
			setCompletedJobStatus(null);
			setPollError(null);
		} else {
			stopPolling();
		}
	}, [open]);

	const stopPolling = () => {
		if (pollRef.current) {
			clearTimeout(pollRef.current);
			pollRef.current = null;
		}
	};

	const startPolling = useCallback(
		(importUuid: string) => {
			const poll = async () => {
				try {
					const status = await fetchXmlImportStatus(importUuid, company);
					setJobStatus(status);

					if (status.status === 'completed' || status.status === 'partial' || status.status === 'failed') {
						setStage('done');
						setCompletedJobStatus(status);
						stopPolling();
						if (status.status === 'completed') {
							showSuccess('Patient record import completed successfully.');
						} else if (status.status === 'partial') {
							showSuccess('Import completed with some warnings — review the results below.');
						}
					} else {
						pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
					}
				} catch {
					setPollError('Lost connection while tracking import progress. Refresh the page to check status.');
					stopPolling();
					setStage('done');
				}
			};
			poll();
		},
		[company, showSuccess],
	);

	const detectedFormat = selectedFile ? detectFormat(selectedFile.name) : null;

	const rejectionReason = (file: File): string | null => {
		if (!ACCEPTED_EXTENSIONS.includes(extensionOf(file.name))) {
			return `Unsupported file type. Upload a patient record in one of these formats: ${ACCEPTED_EXTENSIONS.join(', ')}.`;
		}
		if (file.size > MAX_FILE_BYTES) {
			return 'File is larger than the 50 MB limit. Upload a smaller export.';
		}
		return null;
	};

	const handleUpload = async () => {
		if (!selectedFile) return;

		setUploading(true);
		setStage('uploading');

		try {
			const result = await submitXmlImport(selectedFile, mode, company, existingPatientUuid);
			setStage('polling');
			startPolling(result.import_uuid);
		} catch (err: any) {
			const msg =
				err?.response?.data?.message ||
				err?.response?.data?.error ||
				'Failed to submit import file';
			showError(msg);
			setStage('select');
		} finally {
			setUploading(false);
		}
	};

	const handleClose = () => {
		stopPolling();
		// If closing from success modal, call the completion callback
		if (stage === 'done' && completedJobStatus) {
			onImportComplete?.(completedJobStatus);
		}
		onClose();
	};

	// ── Render helpers ──────────────────────────────────────────────────────────

	const renderSelect = () => (
		<>
			{mode === 'existing_patient' && existingPatientName && (
				<Alert
					type="info"
					showIcon
					message={`Importing into existing patient: ${existingPatientName}`}
					style={{ marginBottom: 16 }}
				/>
			)}
			{mode === 'new_patient' && (
				<Alert
					type="info"
					showIcon
					message="A new patient account will be created from the uploaded patient record."
					style={{ marginBottom: 16 }}
				/>
			)}

			<Dragger
				name="file"
				accept=".pdf,.xml,.txt,application/pdf,text/xml,application/xml,text/plain"
				beforeUpload={(file) => {
					const rejection = rejectionReason(file);
					if (rejection) {
						setSelectedFile(null);
						showError(rejection);
						return Upload.LIST_IGNORE;
					}
					setSelectedFile(file);
					return false; // prevent auto-upload
				}}
				onRemove={() => setSelectedFile(null)}
				maxCount={1}
				style={{ padding: '8px 0' }}
			>
				<p className="ant-upload-drag-icon">
					<InboxOutlined style={{ color: 'var(--mg-primary)' }} />
				</p>
				<p className="ant-upload-text">Click or drag a patient record here</p>
				<p className="ant-upload-hint" style={{ fontSize: 12 }}>
					Accepts .pdf, .xml and .txt files — a PDF patient record, or a Medical Director XML export
					(File → Export → Patient Export). Maximum 50 MB.
				</p>
			</Dragger>

			{selectedFile && detectedFormat && (
				<Alert
					type="success"
					showIcon
					style={{ marginTop: 16 }}
					message={`Detected format: ${FORMAT_LABELS[detectedFormat]}`}
					description={
						detectedFormat === 'pdf'
							? 'A PDF is read page by page, so this import usually takes several minutes.'
							: undefined
					}
				/>
			)}
		</>
	);

	const renderPolling = () => (
		<Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center', padding: '24px 0' }}>
			<Spin size="large" />
			<div>
				<Title level={5} style={{ marginBottom: 4 }}>Import in progress…</Title>
				<Text type="secondary">
					{detectedFormat === 'pdf'
						? 'Reading a PDF record page by page usually takes several minutes. You can close this window — the import will continue in the background.'
						: 'This may take a minute. You can close this window — the import will continue in the background.'}
				</Text>
			</div>
			{jobStatus && (
				<Progress
					percent={
						jobStatus.sections_total > 0
							? Math.round(((jobStatus.sections_completed + jobStatus.sections_failed) / jobStatus.sections_total) * 100)
							: 0
					}
					status="active"
					style={{ maxWidth: 400, margin: '0 auto' }}
				/>
			)}
			<ImportCarousel />
		</Space>
	);

	const footer = () => {
		if (stage === 'done') {
			return (
				<Button onClick={handleClose} type="primary">
					Close
				</Button>
			);
		}
		if (stage === 'polling') {
			return (
				<Button onClick={handleClose} type="default">
					Close (import continues in background)
				</Button>
			);
		}
		return (
			<Space>
				<Button onClick={handleClose}>Cancel</Button>
				<Button
					type="primary"
					icon={<FileSyncOutlined />}
					onClick={handleUpload}
					disabled={!selectedFile}
					loading={uploading}
				>
					Import Patient
				</Button>
			</Space>
		);
	};

	return (
		<Modal
			open={open}
			title={
				<Space>
					<FileSyncOutlined style={{ color: 'var(--mg-primary)' }} />
					<span>Import Patient Record</span>
				</Space>
			}
			onCancel={handleClose}
			footer={footer()}
			width={800}
			destroyOnClose
		>
			{stage === 'select'    && renderSelect()}
			{stage === 'uploading' && <div style={{ textAlign: 'center', padding: '32px 0' }}><Spin size="large" tip="Uploading file…" /></div>}
			{stage === 'polling'   && renderPolling()}
			{stage === 'done'      && <ImportSuccessModal jobStatus={jobStatus} pollError={pollError} />}
		</Modal>
	);
};

export default MedicalDirectorImportModal;
